import { useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { formatDate } from '@werkdelta/shared';
import { Feld, Fehler, InfoTip } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import type { StepProps } from '../CasePage';

const STATUSES = [
  'VERLANGT',
  'ANGEBOTEN',
  'VERSUCHT',
  'VERWEIGERT',
  'ERFOLGREICH',
  'TEILWEISE',
  'UNVERHAELTNISMAESSIG_BEHAUPTET',
];

/** Schritt 6: Nachbesserungsverlauf (Auftrag 7.6). */
export function Step6Nachbesserung({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'remediation-events', reload);
  const [form, setForm] = useState({
    defectFindingId: '',
    contractRelationId: '',
    status: 'VERLANGT',
    eventDate: '',
    description: '',
  });

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      defectFindingId: form.defectFindingId || undefined,
      contractRelationId: form.contractRelationId || undefined,
      status: form.status,
      eventDate: form.eventDate || undefined,
      description: form.description || undefined,
    });
    if (ok) setForm({ ...form, status: 'VERLANGT', eventDate: '', description: '' });
  }

  return (
    <>
      <section className="karte">
        <h2>
          {t('step6.title')}
          <InfoTip termKey="nachbesserungskosten" />
        </h2>
        <p className="erklaerung">{t('step6.intro')}</p>
        {data.remediationEvents.length === 0 ? (
          <div className="leer-hinweis">{t('step6.empty')}</div>
        ) : (
          <div className="tabelle-scroll">
          <table className="daten">
            <thead>
              <tr>
                <th>{t('common.date')}</th>
                <th>{t('common.status')}</th>
                <th>{t('step6.defect')}</th>
                <th>{t('common.description')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.remediationEvents.map((ev: any) => (
                <tr key={ev.id}>
                  <td>{ev.eventDate ? formatDate(ev.eventDate) : '—'}</td>
                  <td>
                    <span className="badge">{t(`remediation.status.${ev.status}`)}</span>
                  </td>
                  <td>
                    {data.defectFindings.find((d: any) => d.id === ev.defectFindingId)?.title ?? '—'}
                  </td>
                  <td>{ev.description ?? '—'}</td>
                  <td>
                    {!readonly && (
                      <button
                        type="button"
                        className="knopf-sekundaer knopf-klein"
                        onClick={() => actions.remove(ev.id)}
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {!readonly && (
        <section className="karte">
          <h3>{t('step6.add')}</h3>
          <form onSubmit={add}>
            <div className="feld-zeile">
              <Feld label={t('common.status')} required>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`remediation.status.${s}`)}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('common.date')}>
                <input
                  type="date"
                  value={form.eventDate}
                  onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                />
              </Feld>
            </div>
            <div className="feld-zeile">
              <Feld label={t('step6.defect')}>
                <select
                  value={form.defectFindingId}
                  onChange={(e) => setForm({ ...form, defectFindingId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.defectFindings.map((d: any) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('step3.relationRef')}>
                <select
                  value={form.contractRelationId}
                  onChange={(e) => setForm({ ...form, contractRelationId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.contractRelations.map((rel: any) => (
                    <option key={rel.id} value={rel.id}>
                      {rel.label}
                    </option>
                  ))}
                </select>
              </Feld>
            </div>
            <Feld label={t('common.description')}>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Feld>
            <Fehler code={actions.errorCode} />
            <div className="aktionen">
              <button type="submit" className="knopf-primaer">
                {t('common.add')}
              </button>
            </div>
          </form>
        </section>
      )}
    </>
  );
}
